import React, { useState, useEffect } from "react";
import {
  MdAdd,
  MdChevronLeft,
  MdChevronRight,
  MdFace,
  MdAutoAwesome,
  MdOutlinePhotoLibrary,
  MdSync,
  MdPeople,
} from "react-icons/md";
import "./ModelListing.css";
import { useModelListing } from "../../customHook/ModelListing/useModelListing";
import SearchBar from "../../components/SearchBar/SearchBar";
import ModelUploadModal from "../../components/ModelUploadModal/ModelUploadModal";
import UserImageUploadModal from "../../components/UserImageUploadModal/UserImageUploadModal";
import InternalPagination from "../../components/InternalPagination/InternalPagination";
import Toggle from "../../components/Toggle/Toggle";
import ZoomModal from "../../components/ZoomModal/ZoomModal";
import ZoomButton from "../../components/buttons/ZoomButton";
import EditButton from "../../components/buttons/EditButton";
import ModelGenerationDrawer from "../../components/ModelGenerationDrawer/ModelGenerationDrawer";
import AIGenerationsView from "../../components/AIGenerationsView/AIGenerationsView";
import { useModelGenerator } from "../../customHook/ModelGeneration/useModelGenerator";

const ExpandableTitle = ({ title = "", className = "" }) => {
  const [showFullTitle, setShowFullTitle] = useState(false);

  return (
    <h3
      className={`text-xs font-bold text-slate-900 cursor-pointer leading-snug transition-all duration-200 min-w-0 flex-1 m-0 hover:text-indigo-600 ${
        showFullTitle ? "whitespace-normal break-words" : "truncate"
      } ${className}`}
      onClick={(e) => {
        e.stopPropagation();
        setShowFullTitle((prev) => !prev);
      }}
      title={showFullTitle ? "Click to collapse" : "Click to view full name"}
    >
      {title}
    </h3>
  );
};

const ModelCardItem = ({
  item,
  isUserType,
  isAdmin,
  onToggle,
  onEdit,
  onZoom,
}) => {
  const [currentImgIndex, setCurrentImgIndex] = useState(0);

  const images = Array.isArray(item.images) && item.images.length > 0
    ? item.images
    : [item.front || item.image, item.back].filter(Boolean);

  const currentImg = images[currentImgIndex] || images[0] || "";
  const hasMultiple = images.length > 1;

  const handlePrev = (e) => {
    e.stopPropagation();
    setCurrentImgIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setCurrentImgIndex((prev) => (prev + 1) % images.length);
  };

  return (
    <div className={`model-item-card ${!item.bIsActive ? "model-item-inactive" : ""}`}>

      {/* Edit Button - absolute top-right overlay (matching Backgrounds) */}
      {isAdmin && (
        <EditButton
          onClick={(e) => {
            e.stopPropagation();
            onEdit(item);
          }}
          positionClasses="absolute top-1.5 right-1.5 z-10"
          size="md"
        />
      )}

      {/* Image Area */}
      <div className="model-image-wrapper group/img">
        {currentImg ? (
          <img
            src={currentImg}
            alt={item.name}
            className={`model-card-img ${isUserType ? "model-card-img-user" : ""}`}
            loading="lazy"
          />
        ) : (
          <div className="flex flex-col items-center justify-center text-slate-400 text-xs gap-1">
            <MdFace size={30} className="opacity-40" />
            <span>No Image</span>
          </div>
        )}

        {/* Tier / Source Badge */}
        {(item.source === "ai" || item.tier === "AI Studio") ? (
          <div className="absolute top-1.5 left-1.5 z-10">
            <span className="inline-flex items-center gap-1 text-[9px] font-medium uppercase tracking-tight px-1.5 py-1 rounded bg-black/60 backdrop-blur-xs text-white border border-white/10 shadow-sm leading-none">
              <MdAutoAwesome size={10} className="text-amber-300" />
              <span>AI Studio</span>
            </span>
          </div>
        ) : item.tier ? (
          <div className="absolute top-1.5 left-1.5 z-10">
            <span className="inline-flex items-center text-[9px] font-medium uppercase tracking-tight px-1.5 py-1 rounded bg-black/60 backdrop-blur-xs text-white border border-white/10 shadow-sm leading-none">
              {item.tier}
            </span>
          </div>
        ) : null}

        {/* Multi-angle Arrows */}
        {hasMultiple && (
          <>
            <button
              type="button"
              onClick={handlePrev}
              className="absolute left-1.5 top-1/2 -translate-y-1/2 z-10 w-6 h-6 rounded-full bg-white/90 backdrop-blur-xs text-slate-800 flex items-center justify-center hover:bg-white shadow-md transition-all cursor-pointer"
              aria-label="Previous image"
            >
              <MdChevronLeft size={16} />
            </button>
            <button
              type="button"
              onClick={handleNext}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 z-10 w-6 h-6 rounded-full bg-white/90 backdrop-blur-xs text-slate-800 flex items-center justify-center hover:bg-white shadow-md transition-all cursor-pointer"
              aria-label="Next image"
            >
              <MdChevronRight size={16} />
            </button>
            {/* Dots */}
            <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex gap-1 z-10">
              {images.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    idx === currentImgIndex ? "bg-slate-900 w-3" : "bg-white/70"
                  }`}
                />
              ))}
            </div>
          </>
        )}

        {/* Zoom Action */}
        {currentImg && (
          <ZoomButton
            modalTarget="#modelListingZoomModal"
            onClick={(e) => {
              e?.stopPropagation();
              onZoom(currentImg, images[(currentImgIndex + 1) % images.length] || item.back);
            }}
            positionClasses="absolute bottom-1.5 right-1.5 z-10"
            size="md"
          />
        )}
      </div>

      {/* Card Footer — name + toggle on one line */}
      <div className="px-2 sm:px-2.5 py-2 flex items-center justify-between gap-1.5 sm:gap-2">
        <ExpandableTitle title={item.name} className="flex-1 min-w-0" />
        <div className="shrink-0" title={item.bIsActive ? "Active" : "Inactive"} onClick={(e) => e.stopPropagation()}>
          <Toggle
            checked={Boolean(item.bIsActive)}
            onChange={() => onToggle(item)}
            size="sm"
          />
        </div>
      </div>
    </div>
  );
};

const ModelListing = () => {
  const {
    userRole,
    modelType,
    onModelTypeChange,
    gender,
    onGenderChange,
    source,
    onSourceChange,
    searchTerm,
    onSearchChange,
    models,
    loading,
    error,
    refreshModels,
    pagination,
    onPageChange,
    onLimitChange,
    handleToggleModel,
    handleDeleteModel,
    addModelLocally,
    // Modals
    isModalOpen,
    modelToEdit,
    isUploadLoading,
    handleOpenUploadModal,
    handleOpenEditModal,
    handleCloseModal,
    handleUploadSubmit,
    // Zoom
    zoomModal,
    openZoomModal,
    closeZoomModal,
  } = useModelListing();

  const [activeMainTab, setActiveMainTab] = useState("library"); // "library" | "ai-studio"
  const [isGenDrawerOpen, setIsGenDrawerOpen] = useState(false);
  const [isUserUploadModalOpen, setIsUserUploadModalOpen] = useState(false);

  const {
    genJobs,
    genHistory,
    historyStats: genStats,
    pagination: genPagination,
    filters: genFilters,
    isFetchingHistory: isGenFetching,
    fetchHistory: fetchGenHistory,
    startGeneration,
    retryJob,
    cancelJob,
    dismissJob,
    saveToLibrary,
    deleteHistoryItem,
  } = useModelGenerator({
    onAddModelLocally: addModelLocally,
  });

  const generatingCount = genJobs.filter((j) => j.status === "generating").length;
  const isAdmin = userRole === "admin" || userRole === "superadmin";

  React.useEffect(() => {
    if (activeMainTab === "ai-studio") {
      fetchGenHistory({ page: 1, limit: genPagination?.limit || 10 });
    }
  }, [activeMainTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddClick = () => {
    if (modelType === "user") {
      setIsUserUploadModalOpen(true);
    } else {
      handleOpenUploadModal();
    }
  };

  const pageHeaderElement = (
    <div className="listing-header-panel px-4 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5 sm:gap-3 shrink-0">
      <div>
        <div className="flex items-center gap-2.5">
          <h1 className="text-[20px] font-bold text-slate-900 tracking-tight m-0">
            Models
          </h1>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-slate-100 text-slate-700 border border-slate-200">
            <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
            {pagination.total} {pagination.total === 1 ? "Model" : "Models"}
          </span>
        </div>
        <p className="text-xs text-slate-500 mt-0.5">
          Manage studio models, digital fashion avatars, and generated AI personas.
        </p>
      </div>

      {/* Header Actions: Segmented Tab Switcher + Action Button */}
      <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-3 w-full sm:w-auto">
        {/* Segmented Tab Switcher: Dark Active Design with Standard Corners */}
        <div className="flex items-center bg-slate-100/90 border border-slate-200/90 p-0.5 rounded gap-0.5 shadow-2xs">
          {/* Library Tab */}
          <button
            type="button"
            onClick={() => {
              if (activeMainTab === "library") {
                refreshModels();
              } else {
                setActiveMainTab("library");
              }
            }}
            className={`flex items-center gap-1 sm:gap-2 px-2.5 sm:px-3 py-1.5 rounded text-xs transition-all duration-150 cursor-pointer ${
              activeMainTab === "library"
                ? "bg-primary text-white font-bold shadow-xs border-none"
                : "bg-white/70 hover:bg-white text-slate-600 hover:text-primary border border-slate-200/80 hover:border-slate-300 font-semibold shadow-2xs"
            }`}
          >
            <MdOutlinePhotoLibrary
              size={15}
              className={activeMainTab === "library" ? "text-white" : "text-slate-500"}
            />
            <span>Library</span>
            <span
              className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold transition-colors ${
                activeMainTab === "library"
                  ? "bg-white/20 text-white border border-white/20"
                  : "bg-slate-200 text-slate-800 border border-slate-200/80"
              }`}
            >
              {pagination?.total || 0}
            </span>
          </button>

          {/* AI Studio Tab */}
          <button
            type="button"
            onClick={() => {
              if (activeMainTab === "ai-studio") {
                fetchGenHistory({ page: 1, limit: genPagination?.limit || 10 });
              } else {
                setActiveMainTab("ai-studio");
              }
            }}
            className={`flex items-center gap-1 sm:gap-2 px-2.5 sm:px-3 py-1.5 rounded text-xs transition-all duration-150 cursor-pointer ${
              activeMainTab === "ai-studio"
                ? "bg-primary text-white font-bold shadow-xs border-none"
                : "bg-white/70 hover:bg-white text-slate-600 hover:text-primary border border-slate-200/80 hover:border-slate-300 font-semibold shadow-2xs"
            }`}
          >
            <MdAutoAwesome
              size={14}
              className={
                generatingCount > 0
                  ? "text-amber-300 animate-spin"
                  : activeMainTab === "ai-studio"
                  ? "text-white"
                  : "text-slate-500"
              }
            />
            <span>AI Studio</span>
            {generatingCount === 0 && (
              <span
                className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold transition-colors ${
                  activeMainTab === "ai-studio"
                    ? "bg-white/20 text-white border border-white/20"
                    : "bg-slate-200 text-slate-800 border border-slate-200/80"
                }`}
              >
                {genStats?.total ?? genPagination?.total ?? genHistory.length}
              </span>
            )}
          </button>
        </div>

        {/* Context-aware Action Button: Styled to match Actions */}
        {isAdmin && (
          activeMainTab === "library" ? (
            <button
              type="button"
              onClick={handleAddClick}
              className="app-button-secondary flex items-center justify-center gap-1 sm:gap-1.5 px-2.5 sm:px-3.5 h-8 rounded-md text-xs font-semibold bg-white border border-slate-300 hover:border-slate-400 text-slate-700 shadow-sm focus:outline-none cursor-pointer whitespace-nowrap shrink-0 transition-all"
              title="Add Model"
            >
              <MdAdd size={16} />
              <span className="hidden sm:inline">Add Model</span>
              <span className="sm:hidden">Add</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setIsGenDrawerOpen(true)}
              className="app-button-secondary flex items-center justify-center gap-1 sm:gap-1.5 px-2.5 sm:px-3.5 h-8 rounded-md text-xs font-semibold bg-white border border-slate-300 hover:border-slate-400 text-slate-700 shadow-sm focus:outline-none cursor-pointer whitespace-nowrap shrink-0 transition-all"
              title="Generate Model"
            >
              <MdAutoAwesome size={15} />
              <span className="hidden sm:inline">Generate Model</span>
              <span className="sm:hidden">Generate</span>
            </button>
          )
        )}
      </div>
    </div>
  );

  return (
    <div className="model-listing-page flex-1 flex flex-col min-h-0 overflow-hidden">
      {/* TAB 1: LIBRARY OF MODELS */}
      {activeMainTab === "library" && (
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {/* Scrollable Content Body */}
          <div className="flex-1 overflow-y-auto min-h-0">
            {pageHeaderElement}

        {/* Controls Bar (Sticky at top when scrolling) */}
        <div className="product-controls-wrapper sticky top-0 z-30 px-4 sm:px-6 py-2 bg-[#F4F6FA]/95 backdrop-blur-sm">
          <div className="app-control-bar product-controls-shell flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 sm:gap-4 p-2.5 sm:p-3">
            <div className="flex flex-wrap items-center gap-2 shrink-0">
              {/* Model Type Selector */}
              <div className="gender-tabs flex gap-0.5 bg-slate-100/80 p-0.5 rounded-md">
                <button
                  type="button"
                  onClick={() => onModelTypeChange("model")}
                  className={`px-2.5 sm:px-4 py-1 rounded font-semibold text-xs transition-all duration-200 ${
                    modelType === "model"
                      ? "bg-primary text-on-primary shadow-sm font-bold border-none"
                      : "bg-white text-slate-500 border border-slate-200 hover:text-primary hover:border-slate-300 hover:shadow-sm"
                  } focus:outline-none`}
                >
                  Studio<span className="hidden sm:inline"> Models</span>
                </button>
                <button
                  type="button"
                  onClick={() => onModelTypeChange("user")}
                  className={`px-2.5 sm:px-4 py-1 rounded font-semibold text-xs transition-all duration-200 ${
                    modelType === "user"
                      ? "bg-primary text-on-primary shadow-sm font-bold border-none"
                      : "bg-white text-slate-500 border border-slate-200 hover:text-primary hover:border-slate-300 hover:shadow-sm"
                  } focus:outline-none`}
                >
                  Users
                </button>
              </div>

              {/* Gender Selector */}
              <div className="gender-tabs flex gap-0.5 bg-slate-100/80 p-0.5 rounded-md">
                <button
                  type="button"
                  onClick={() => onGenderChange("women")}
                  className={`px-3 sm:px-4 py-1 rounded font-semibold text-xs transition-all duration-200 ${
                    gender === "women"
                      ? "bg-primary text-on-primary shadow-sm font-bold border-none"
                      : "bg-white text-slate-500 border border-slate-200 hover:text-primary hover:border-slate-300 hover:shadow-sm"
                  } focus:outline-none`}
                >
                  Women
                </button>
                <button
                  type="button"
                  onClick={() => onGenderChange("men")}
                  className={`px-3 sm:px-4 py-1 rounded font-semibold text-xs transition-all duration-200 ${
                    gender === "men"
                      ? "bg-primary text-on-primary shadow-sm font-bold border-none"
                      : "bg-white text-slate-500 border border-slate-200 hover:text-primary hover:border-slate-300 hover:shadow-sm"
                  } focus:outline-none`}
                >
                  Men
                </button>
              </div>

              {/* Source Selector: All, Upload, AI */}
              <div className="gender-tabs flex gap-0.5 bg-slate-100/80 p-0.5 rounded-md">
                <button
                  type="button"
                  onClick={() => onSourceChange("all")}
                  className={`px-2.5 sm:px-3 py-1 rounded font-semibold text-xs transition-all duration-200 ${
                    source === "all"
                      ? "bg-primary text-on-primary shadow-sm font-bold border-none"
                      : "bg-white text-slate-500 border border-slate-200 hover:text-primary hover:border-slate-300 hover:shadow-sm"
                  } focus:outline-none`}
                >
                  All Sources
                </button>
                <button
                  type="button"
                  onClick={() => onSourceChange("upload")}
                  className={`px-2.5 sm:px-3 py-1 rounded font-semibold text-xs transition-all duration-200 ${
                    source === "upload"
                      ? "bg-primary text-on-primary shadow-sm font-bold border-none"
                      : "bg-white text-slate-500 border border-slate-200 hover:text-primary hover:border-slate-300 hover:shadow-sm"
                  } focus:outline-none`}
                >
                  Uploads
                </button>
                <button
                  type="button"
                  onClick={() => onSourceChange("ai")}
                  className={`px-2.5 sm:px-3 py-1 rounded font-semibold text-xs transition-all duration-200 flex items-center gap-1 ${
                    source === "ai"
                      ? "bg-primary text-on-primary shadow-sm font-bold border-none"
                      : "bg-white text-slate-500 border border-slate-200 hover:text-primary hover:border-slate-300 hover:shadow-sm"
                  } focus:outline-none`}
                >
                  <MdAutoAwesome size={12} className={source === "ai" ? "text-amber-300" : "text-amber-500"} />
                  <span>AI Studio</span>
                </button>
              </div>
            </div>

            {/* Search & Mobile Action Button */}
            <div className="product-search-actions flex items-center gap-1.5 sm:gap-2 sm:justify-end relative min-w-0 w-full sm:w-auto">
              <SearchBar
                value={searchTerm}
                onChange={onSearchChange}
                placeholder="Search models by name..."
                className="flex-1 min-w-0 w-full sm:w-64"
              />
              {isAdmin && (
                <button
                  type="button"
                  onClick={handleAddClick}
                  className="sm:hidden app-button-primary flex items-center justify-center gap-1 px-2.5 h-8 rounded-md text-xs font-semibold shadow-xs cursor-pointer whitespace-nowrap shrink-0"
                  title="Add Model"
                >
                  <MdAdd size={16} />
                  <span>Add</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Card Grid */}
        <div className="px-4 sm:px-6 pt-4 sm:pt-5 pb-10">
          {loading ? (
            <div className="flex-1 flex flex-col items-center justify-center py-20 w-full text-center">
              <MdSync className="text-[36px] text-slate-400 animate-spin mb-2" />
              <p className="text-sm font-semibold text-secondary">Loading models catalog...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-20 text-center bg-red-50 border border-red-200 rounded-xl p-6">
              <p className="text-sm font-semibold text-red-600 mb-2">{error}</p>
              <button
                type="button"
                onClick={refreshModels}
                className="px-3 py-1.5 bg-white border border-red-300 rounded-lg text-xs font-semibold text-red-700 shadow-xs hover:bg-red-50"
              >
                Retry
              </button>
            </div>
          ) : models.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center bg-white/70 border border-slate-200/80 rounded-xl">
              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 mb-3">
                <MdPeople size={24} />
              </div>
              <h3 className="text-sm font-bold text-slate-800 mb-1">No models found</h3>
              <p className="text-xs text-slate-500 max-w-sm mb-0">
                Try searching for another name or adding a new model to this category.
              </p>
            </div>
          ) : (
            <div className="model-card-grid listing-animate-entry items-start">
              {models.map((model) => (
                <ModelCardItem
                  key={model.id}
                  item={model}
                  isUserType={modelType === "user"}
                  isAdmin={isAdmin}
                  onToggle={handleToggleModel}
                  onEdit={handleOpenEditModal}
                  onZoom={openZoomModal}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Always Visible Footer Bar */}
      <footer className="app-footer-bar shrink-0 border-t border-slate-200/90 bg-white/95 backdrop-blur-sm px-3 sm:px-6 py-1.5 z-20 shadow-[0_-2px_8px_rgba(26,32,48,0.03)] flex items-center min-h-[38px] overflow-x-auto">
        <InternalPagination
          page={pagination?.page || 1}
          limit={pagination?.limit || 10}
          total={pagination?.total || 0}
          totalPages={pagination?.totalPages || 1}
          limitOptions={[10, 30, 50]}
          onPageChange={onPageChange}
          onLimitChange={onLimitChange}
          showFirstLast={true}
          rowsLabel="Items per page:"
        />
      </footer>
    </div>
  )}

  {/* TAB 2: AI GENERATIONS & QUEUE */}
  {activeMainTab === "ai-studio" && (
    <AIGenerationsView
      header={pageHeaderElement}
      jobs={genJobs}
      history={genHistory}
      historyStats={genStats}
      pagination={genPagination}
      filters={genFilters}
      isFetching={isGenFetching}
      onFetchHistory={fetchGenHistory}
      onRetry={retryJob}
      onCancel={cancelJob}
      onDismiss={dismissJob}
      onSaveToLibrary={saveToLibrary}
      onZoom={(img) => openZoomModal(img, "")}
      onOpenDrawer={() => setIsGenDrawerOpen(true)}
      onDeleteHistoryItem={deleteHistoryItem}
      modalTarget="#modelListingZoomModal"
      entityType="model"
      entityLabel="Model"
      pluralLabel="Models"
      cardAspect="aspect-[4/5]"
      createButtonText="Generate Model"
    />
  )}

  {/* Model Upload / Edit Modal */}
  <ModelUploadModal
    isOpen={isModalOpen}
    onClose={handleCloseModal}
    onSubmit={handleUploadSubmit}
    isLoading={isUploadLoading}
    modelToEdit={modelToEdit}
    defaultGender={gender}
    defaultSType={modelType}
  />

  {/* User Persona Upload Modal */}
  <UserImageUploadModal
    isOpen={isUserUploadModalOpen}
    onClose={() => setIsUserUploadModalOpen(false)}
    onSubmit={async (payload) => {
      await handleUploadSubmit(payload);
      setIsUserUploadModalOpen(false);
    }}
    isLoading={isUploadLoading}
    defaultGender={gender}
  />

  {/* Zoom Modal */}
  <ZoomModal
    modalId="modelListingZoomModal"
    front={zoomModal.front}
    back={zoomModal.back}
    onClose={closeZoomModal}
  />

  {/* Model Generation Drawer */}
  <ModelGenerationDrawer
    isOpen={isGenDrawerOpen}
    onClose={() => setIsGenDrawerOpen(false)}
    onGenerate={async (params) => {
      const res = await startGeneration(params);
      if (res?.success) {
        setIsGenDrawerOpen(false);
        setActiveMainTab("ai-studio"); // Switch to AI tab so user watches progress!
      }
      return res;
    }}
    defaultGender=""
    activeJobsCount={generatingCount}
  />
</div>
  );
};

export default ModelListing;
